import {ref, computed} from 'vue'
import {usersAPI} from '@/services/api'

// Global cache for profile pictures (shared across components)
const pictureCache = ref<Record<string, string | null>>({})
const isUploading = ref(false)

/**
 * Composable for profile picture management.
 * Provides upload, remove, and display functionality with client-side image optimization.
 */
export function useProfilePicture() {
    const uploadError = ref('')

    /**
     * Resize an image file to a max dimension and convert to base64 data URL.
     * Uses canvas for client-side compression.
     */
    function resizeImage(file: File, maxSize = 128, quality = 0.85): Promise<string> {
        return new Promise((resolve, reject) => {
            if (!file.type.match(/^image\/(jpeg|png|webp)$/)) {
                reject(new Error('Only JPEG, PNG, and WebP images are allowed'))
                return
            }

            if (file.size > 5 * 1024 * 1024) {
                reject(new Error('Image must be under 5MB'))
                return
            }

            const reader = new FileReader()
            reader.onload = (e) => {
                const img = new Image()
                img.onload = () => {
                    const canvas = document.createElement('canvas')
                    let width = img.width
                    let height = img.height

                    // Crop to square (center crop)
                    const size = Math.min(width, height)
                    const sx = (width - size) / 2
                    const sy = (height - size) / 2

                    canvas.width = maxSize
                    canvas.height = maxSize

                    const ctx = canvas.getContext('2d')
                    if (!ctx) {
                        reject(new Error('Canvas not supported'))
                        return
                    }

                    // Draw with smooth scaling
                    ctx.imageSmoothingEnabled = true
                    ctx.imageSmoothingQuality = 'high'
                    ctx.drawImage(img, sx, sy, size, size, 0, 0, maxSize, maxSize)

                    const dataUrl = canvas.toDataURL('image/jpeg', quality)
                    resolve(dataUrl)
                }
                img.onerror = () => reject(new Error('Failed to load image'))
                img.src = e.target?.result as string
            }
            reader.onerror = () => reject(new Error('Failed to read file'))
            reader.readAsDataURL(file)
        })
    }

    /**
     * Upload a profile picture from a File input.
     */
    async function uploadPicture(file: File): Promise<string | null> {
        uploadError.value = ''
        isUploading.value = true

        try {
            const dataUrl = await resizeImage(file)
            const response = await usersAPI.uploadProfilePicture(dataUrl)
            const picture = response.data.profilePicture

            // Update cache
            const userEmail = getCurrentUserEmail()
            if (userEmail) {
                pictureCache.value[userEmail] = picture
            }

            return picture
        } catch (error: any) {
            uploadError.value = error.response?.data?.error || error.message || 'Upload failed'
            return null
        } finally {
            isUploading.value = false
        }
    }

    /**
     * Remove the current user's profile picture.
     */
    async function removePicture(): Promise<boolean> {
        uploadError.value = ''
        try {
            await usersAPI.removeProfilePicture()
            const userEmail = getCurrentUserEmail()
            if (userEmail) {
                pictureCache.value[userEmail] = null
            }
            return true
        } catch (error: any) {
            uploadError.value = error.response?.data?.error || error.message || 'Remove failed'
            return false
        }
    }

    /**
     * Get cached profile picture for an email.
     */
    function getPicture(email: string): string | null {
        return pictureCache.value[email.toLowerCase()] ?? null
    }

    /**
     * Fetch profile pictures for a set of emails (batch).
     */
    async function fetchPictures(emails: string[]) {
        const uncached = emails.filter(e => !(e.toLowerCase() in pictureCache.value))
        if (!uncached.length) return

        try {
            const response = await usersAPI.getProfilePictures(uncached)
            const data = response.data || {}
            for (const [email, pic] of Object.entries(data)) {
                pictureCache.value[email.toLowerCase()] = pic as string | null
            }
            // Mark emails without pictures as null (so we don't re-fetch)
            for (const email of uncached) {
                if (!(email.toLowerCase() in pictureCache.value)) {
                    pictureCache.value[email.toLowerCase()] = null
                }
            }
        } catch {
            // Silently fail — will show initials fallback
        }
    }

    /**
     * Set picture directly in cache (e.g., from /me response).
     */
    function setCachedPicture(email: string, picture: string | null) {
        pictureCache.value[email.toLowerCase()] = picture
    }

    return {
        pictureCache,
        isUploading,
        uploadError,
        uploadPicture,
        removePicture,
        getPicture,
        fetchPictures,
        setCachedPicture,
        resizeImage,
    }
}

// Helper to get current user email from auth store
function getCurrentUserEmail(): string | null {
    try {
        // Import dynamically to avoid circular dependencies
        const {useAuthStore} = require('@/stores/authStore')
        const store = useAuthStore()
        return store.userEmail?.toLowerCase() || null
    } catch {
        return null
    }
}
